/**
 * Recently Viewed — server-rendered product cards via Section Rendering API.
 *
 * Follows the same prototype constructor pattern as theme.RecentlyViewed (theme.js:7995).
 * Fetch-parse-inject pattern sourced from ProductRecommendations (theme.js:5484).
 */

(function() {
  'use strict';

  var CACHE_KEY_PREFIX = 'recent-cards';
  var DEFAULT_SECTION_ID = 'recently-viewed-product-card';

  // Module-scoped in-flight promise map. Concurrent fetchCards() calls with
  // identical (sectionId, handles) share the same promise — prevents duplicate
  // network requests when the zero-state cache warmup races with an actual
  // open, or when multiple PredictiveSearch instances coexist on one page.
  var inFlightFetchCards = {};

  // Cards carry rendered prices, so a card cached in one market must never be replayed in another.
  function marketKey() {
    var shopify = window.Shopify || {};
    var currency = shopify.currency && shopify.currency.active;
    return (shopify.country || 'xx') + '-' + (currency || 'xxx');
  }

  // search-fitment.js changes the card markup, so cards cached before it was turned on
  // (or off) must not be replayed after.
  function cacheScope() {
    return (theme.customerState || 'anon') + '-' + marketKey() + (theme.searchFitment ? '-fit' : '');
  }

  function cacheKey(sectionId) {
    return CACHE_KEY_PREFIX + '-' + sectionId + '-' + cacheScope();
  }

  function RecentlyViewed(container) {
    if (!container) return;

    this.container = container;
    this.sectionId = container.getAttribute('data-section-id');
    this.handle = container.getAttribute('data-product-handle');
    this.limit = parseInt(container.getAttribute('data-recent-count'), 10) || 5;
    this.gridItemClass = container.getAttribute('data-grid-item-class');
    this.rowOf = container.getAttribute('data-row-of');
    this.outputContainer = document.getElementById('RecentlyViewed-' + this.sectionId);

    theme.initWhenVisible({
      element: this.container,
      callback: this.init.bind(this),
      threshold: 600
    });
  }

  RecentlyViewed.prototype.init = function() {
    var recentHandles = getRecentHandles();

    if (!recentHandles.length) {
      this.container.classList.add('hide');
      return;
    }

    var filtered = filterHandles(recentHandles, this.handle, this.limit);

    var MIN_PRODUCTS = 2;
    if (filtered.length < MIN_PRODUCTS) {
      this.container.classList.add('hide');
      return;
    }

    this.fetchAndRender(filtered);
  };

  RecentlyViewed.prototype.fetchAndRender = function(handles) {
    fetchCards(handles, DEFAULT_SECTION_ID)
      .then(function(results) {
        this.renderCards(results);
        this.postInjectInit();
      }.bind(this))
      .catch(function() {
        this.container.classList.add('hide');
      }.bind(this));
  };

  RecentlyViewed.prototype.renderCards = function(results) {
    var fragment = document.createDocumentFragment();
    var gridClass = this.gridItemClass;
    var rowOf = this.rowOf;

    results.forEach(function(html) {
      if (!html) return;

      var temp = document.createElement('div');
      temp.innerHTML = html;
      var card = temp.querySelector('.grid__item');

      if (!card) return;

      overrideGridClass(card, gridClass);
      card.setAttribute('data-aos', 'row-of-' + rowOf);
      fragment.appendChild(card);
    });

    if (!fragment.childElementCount) {
      this.container.classList.add('hide');
      return;
    }

    this.outputContainer.innerHTML = '';
    this.outputContainer.appendChild(fragment);
  };

  RecentlyViewed.prototype.postInjectInit = function() {
    theme.reinitProductGridItem(this.container);
    initSwatchHovering(this.outputContainer);
    triggerLooxRescan();
  };

  RecentlyViewed.prototype.onUnload = function() {
    this.outputContainer.innerHTML = '';
  };

  // --- Helpers ---

  function getRecentHandles() {
    var recent = theme.recentlyViewed && theme.recentlyViewed.recent;
    if (!recent || Array.isArray(recent) || typeof recent !== 'object') return [];
    // Keys are in insertion order; newest is last (theme.js:7026-7030 deletes + re-adds)
    return Object.keys(recent).reverse();
  }

  function filterHandles(handles, currentHandle, limit) {
    return handles.filter(function(h) {
      return h && h !== 'undefined' && h !== currentHandle;
    }).slice(0, limit);
  }

  function fetchCardHtml(handle, sectionId, cache) {
    if (cache[handle]) {
      return Promise.resolve(cache[handle]);
    }

    var url = '/products/' + handle + '?section_id=' + sectionId;

    return fetch(url)
      .then(function(res) {
        if (!res.ok) return null;
        return res.text();
      })
      .then(function(html) {
        if (html) cache[handle] = html;
        return html;
      })
      .catch(function() {
        return null;
      });
  }

  function overrideGridClass(card, gridClass) {
    var classes = card.className.split(' ');
    var cleaned = classes.filter(function(c) {
      return !c.match(/^(small--|medium-up--|large-up--|medium--|large--)/);
    });
    cleaned.push(gridClass);
    card.className = cleaned.join(' ');
  }

  function initSwatchHovering(scope) {
    var swatches = scope.querySelectorAll('.color-swatch--with-image');

    swatches.forEach(function(swatch) {
      swatch.addEventListener('mouseenter', function() {
        setActiveColorImage(swatch);
      });
      swatch.addEventListener('mouseleave', function() {
        removeActiveColorImage(swatch);
      });
    });
  }

  function setActiveColorImage(swatch) {
    var id = swatch.dataset.variantId;
    var image = swatch.dataset.variantImage;
    var product = swatch.closest('.grid-product__content');

    if (!id || !image || !product) return;

    var colorImages = product.querySelectorAll('.grid-product__color-image');
    colorImages.forEach(function(el) { el.classList.remove('is-active'); });

    var siblings = product.querySelectorAll('.color-swatch--with-image');
    siblings.forEach(function(el) { el.classList.remove('is-active'); });

    var imageEl = product.querySelector('.grid-product__color-image--' + id);
    if (imageEl) {
      imageEl.style.backgroundImage = 'url(' + image + ')';
      imageEl.classList.add('is-active');
    }
    swatch.classList.add('is-active');
  }

  function removeActiveColorImage(swatch) {
    var id = swatch.dataset.variantId;
    var product = swatch.closest('.grid-product__content');
    if (!id || !product) return;

    var imageEl = product.querySelector('.grid-product__color-image--' + id);
    if (imageEl) imageEl.classList.remove('is-active');
  }

  function triggerLooxRescan() {
    try {
      if (typeof looxProtocol !== 'undefined' && looxProtocol.run) {
        looxProtocol.run();
        return;
      }
    } catch (e) { /* noop */ }

    try {
      if (typeof LooxReviews !== 'undefined' && LooxReviews.init) {
        LooxReviews.init();
        return;
      }
    } catch (e) { /* noop */ }

    document.dispatchEvent(new CustomEvent('loox:domchanged'));
  }

  function readCache(sectionId) {
    if (!theme.config.hasSessionStorage) return {};
    try {
      var raw = sessionStorage.getItem(cacheKey(sectionId));
      return raw ? JSON.parse(raw) : {};
    } catch (e) {
      return {};
    }
  }

  function writeCache(sectionId, cache, handles) {
    if (!theme.config.hasSessionStorage) return;
    var trimmed = {};
    handles.forEach(function(h) { if (cache[h]) trimmed[h] = cache[h]; });
    try {
      sessionStorage.setItem(cacheKey(sectionId), JSON.stringify(trimmed));
    } catch (e) { /* quota exceeded — non-critical */ }
  }

  // Batched fetch that caches results in sessionStorage under a per-section key,
  // so consumers that render different section markup (PDP recently-viewed vs.
  // predictive-search zero state) don't clobber each other's caches.
  //
  // In-flight promise dedup: concurrent calls with identical (section, handles)
  // share the same promise. This prevents duplicate network requests when the
  // zero-state cache warmup races with a user-triggered open, or when multiple
  // PredictiveSearch instances run warmup simultaneously.
  function fetchCards(handles, sectionId) {
    var section = sectionId || DEFAULT_SECTION_ID;
    var key = section + '|' + handles.join(',');

    if (inFlightFetchCards[key]) return inFlightFetchCards[key];

    var cache = readCache(section);
    var promises = handles.map(function(h) { return fetchCardHtml(h, section, cache); });
    var combined = Promise.all(promises).then(function(results) {
      writeCache(section, cache, handles);
      delete inFlightFetchCards[key];
      return results;
    }, function(err) {
      delete inFlightFetchCards[key];
      throw err;
    });

    inFlightFetchCards[key] = combined;
    return combined;
  }

  // Expose reusable helpers on the shared namespace (populated by theme.js:34-37).
  theme.recentlyViewed = theme.recentlyViewed || {};
  theme.recentlyViewed.getHandles = getRecentHandles;
  theme.recentlyViewed.filterHandles = filterHandles;
  theme.recentlyViewed.fetchCards = fetchCards;
  // Other cached search markup (search-overlay.js best sellers) is scoped the same way.
  theme.recentlyViewed.cacheScope = cacheScope;

  // --- Registration ---
  // Both theme.js and this file load with <script defer>.
  // Defer scripts execute in document order after parsing completes.
  // At that point document.readyState is 'interactive', so theme.js's
  // DOMready() runs its callback synchronously — creating theme.sections
  // before this file even executes. Our readyState check also resolves
  // immediately, finding theme.sections already available.

  function register() {
    if (theme.sections) {
      theme.sections.register('recently-viewed', RecentlyViewed);
    }
  }

  if (document.readyState !== 'loading') {
    register();
  } else {
    document.addEventListener('DOMContentLoaded', register);
  }
})();
